import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Stack,
  Text,
  Loader,
  Center,
  Alert,
  Checkbox,
  SegmentedControl,
  Button,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Info, Download } from 'react-feather';
import { ButtonToggle } from '../../../../components/common';
import type { ButtonToggleOption } from '../../../../components/common/ButtonToggle/ButtonToggle';
import { SurveyConfigCard, type SurveyFormState } from './SurveyConfigCard';
import { ClearConfigurationModal } from './ClearConfigurationModal';
import { PullHistoricalResponsesModal } from './PullHistoricalResponsesModal';
import {
  feedbackSurveysApi,
  type FeedbackSurveyType,
  type FeedbackSurveyConfig,
  type SurveyModalityType,
  type PaperformForm,
  type MergeTag,
  type EmailTemplate,
  type ScheduleTimesResponse,
} from '../../../../services/api/feedbackSurveys';
import {
  intakeApi,
  type IntakeStats,
  type BackfillHistoricalSummary,
} from '../../../../services/api/intake';
import { parseDateOnly, toISODateString } from '../../../../utils/dateHelpers';
import type { CohortWithDetails } from '../../../../types';

type SurveyType = 'matching_form' | 'post_session' | 'midpoint' | 'endpoint';

const SURVEY_TYPE_OPTIONS: { value: SurveyType; label: string }[] = [
  { value: 'matching_form', label: 'Matching Form' },
  { value: 'post_session', label: 'Post Session' },
  { value: 'midpoint', label: 'Midpoint' },
  { value: 'endpoint', label: 'Endpoint' },
];

const DEFAULT_MERGE_TAGS: MergeTag[] = [
  { tag: '{{first_name}}', description: 'Participant first name' },
  { tag: '{{last_name}}', description: 'Participant last name' },
  { tag: '{{email}}', description: 'Participant email' },
  { tag: '{{cohort_name}}', description: 'Cohort/program name' },
  { tag: '{{form_link}}', description: 'Link to survey' },
  { tag: '{{start_date}}', description: 'Program start date' },
  { tag: '{{session_number}}', description: 'Session number' },
  { tag: '{{survey_deadline}}', description: 'Survey deadline date' },
];

// Composite key for form state: post-session uses 'post_session:group_coaching' etc.
function postSessionKey(modality: SurveyModalityType): string {
  return `post_session:${modality}`;
}

const DEFAULT_SUBJECTS: Record<SurveyType, string> = {
  matching_form: 'Complete Your {{cohort_name}} Intake Form',
  post_session: '{{cohort_name}} Session {{session_number}} Survey',
  midpoint: '{{cohort_name}} Midpoint Survey',
  endpoint: '{{cohort_name}} Endpoint Survey',
};

const DEFAULT_BODIES: Record<SurveyType, string> = {
  matching_form: `Hi {{first_name}},

Welcome to {{cohort_name}}! To help us personalize your coaching experience, please complete the intake form below.

This form helps us understand your goals, experience, and preferences so we can match you with the right coach and tailor your sessions accordingly.

Please click the link below to complete your intake form:
{{form_link}}

If you have any questions, please don't hesitate to reach out.

Best regards`,
  post_session: `Hi {{first_name}},

Thank you for attending Session {{session_number}} of {{cohort_name}}.

We value your participation and would love to hear your feedback. It helps us improve the program to best support your development.

**Please Complete This 2-3 Minute Survey**
{{form_link}}

Your responses are confidential and will be used to enhance your coaching experience.

Thank you for your time!`,
  midpoint: `Hi {{first_name}},

We hope you're enjoying the {{cohort_name}} Program so far. We've really valued your participation, and would love to hear how your experience is going.

Please take five minutes to reflect on your experience to date and share your feedback with us.

**Complete The Midpoint Survey**
{{form_link}}

Please complete the survey by {{survey_deadline}}

Thank you`,
  endpoint: `Hi {{first_name}},

We hope you enjoyed the {{cohort_name}} Program. We've really valued your participation, and would love to hear about your experience.

Please take five minutes to reflect on your experience and share your feedback with us. Your feedback will help us improve the program for future cohorts.

**Complete The Endpoint Survey**
{{form_link}}

Please complete the survey by {{survey_deadline}}

Thank you`,
};

const INFO_BANNERS: Record<SurveyType, string> = {
  matching_form: 'Intake form sent to participants before program begins to gather preferences for coach matching.',
  post_session: 'Feedback survey sent automatically after each group coaching session once the coach marks attendance complete.',
  midpoint: 'Mid-program survey sent to all participants after the middle session to assess experience so far.',
  endpoint: 'End-of-program survey sent after the final session to gather overall program feedback.',
};

interface SurveysTabProps {
  cohort: CohortWithDetails;
  isActive?: boolean;
}

export function SurveysTab({ cohort, isActive }: SurveysTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [clearModalOpened, { open: openClearModal, close: closeClearModal }] = useDisclosure(false);
  const [clearing, setClearing] = useState(false);

  // MED-942: Pull Historical Responses modal state. Lives on the SurveysTab
  // because it's a Matching-Form-sub-tab-only feature — SurveyConfigCard
  // stays generic.
  const [
    backfillModalOpened,
    { open: openBackfillModal, close: closeBackfillModal },
  ] = useDisclosure(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillSummary, setBackfillSummary] =
    useState<BackfillHistoricalSummary | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  // Survey type from URL or default
  const surveyParam = searchParams.get('survey') as SurveyType | null;
  const [activeSurveyType, setActiveSurveyType] = useState<SurveyType>(
    surveyParam && SURVEY_TYPE_OPTIONS.some((o) => o.value === surveyParam)
      ? surveyParam
      : 'matching_form'
  );

  // Post-session modality — extract modality_type from cohort's ModalityWithWorkshops[]
  const modalityTypes = cohort.modalities?.map((m) => m.modality_type) || ['group_coaching'];
  const postSessionModalities: SurveyModalityType[] = modalityTypes.filter(
    (m): m is SurveyModalityType => m === 'group_coaching' || m === 'workshop'
  );
  const hasOneOnOne = modalityTypes.includes('one_on_one');
  const [activePostSessionModality, setActivePostSessionModality] = useState<SurveyModalityType>(
    postSessionModalities[0] ?? 'group_coaching'
  );

  // Data from APIs
  const [paperforms, setPaperforms] = useState<PaperformForm[]>([]);
  const [mergeTags, setMergeTags] = useState<MergeTag[]>(DEFAULT_MERGE_TAGS);
  const [defaultTemplates, setDefaultTemplates] = useState<Record<FeedbackSurveyType, EmailTemplate> | null>(null);
  const [feedbackConfigs, setFeedbackConfigs] = useState<Record<string, FeedbackSurveyConfig | null>>({
    [postSessionKey('group_coaching')]: null,
    [postSessionKey('workshop')]: null,
    midpoint: null,
    endpoint: null,
  });
  const [intakeStats, setIntakeStats] = useState<IntakeStats | null>(null);
  const [scheduleTimes, setScheduleTimes] = useState<ScheduleTimesResponse | null>(null);
  const [participantCount, setParticipantCount] = useState(0);

  // Form states keyed by type
  const makeEmptyState = (type: SurveyType): SurveyFormState => ({
    paperform_id: '',
    email_subject: DEFAULT_SUBJECTS[type] || '',
    email_template: DEFAULT_BODIES[type] || '',
    survey_deadline: null,
    auto_send: true,
    isDirty: false,
    isSaved: false,
  });

  const [formStates, setFormStates] = useState<Record<string, SurveyFormState>>({
    matching_form: makeEmptyState('matching_form'),
    [postSessionKey('group_coaching')]: makeEmptyState('post_session'),
    [postSessionKey('workshop')]: makeEmptyState('post_session'),
    midpoint: makeEmptyState('midpoint'),
    endpoint: makeEmptyState('endpoint'),
  });

  // Current form key for post-session (includes modality)
  const currentFormKey = activeSurveyType === 'post_session'
    ? postSessionKey(activePostSessionModality)
    : activeSurveyType;

  const currentState = formStates[currentFormKey] || makeEmptyState(activeSurveyType);

  // Sync survey type with URL
  const handleSurveyTypeChange = useCallback((value: SurveyType) => {
    setActiveSurveyType(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'surveys');
      if (value !== 'matching_form') {
        next.set('survey', value);
      } else {
        next.delete('survey');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Update form state helper
  const updateFormState = useCallback((key: string, updates: Partial<SurveyFormState>) => {
    setFormStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...updates, isDirty: true },
    }));
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [formsRes, tagsRes, templatesRes, configsRes, statusRes, timesRes, statsRes] = await Promise.all([
        feedbackSurveysApi.getPaperforms(),
        feedbackSurveysApi.getMergeTags(),
        feedbackSurveysApi.getDefaultTemplates(),
        feedbackSurveysApi.getConfigs(cohort.id),
        feedbackSurveysApi.getStatus(cohort.id),
        feedbackSurveysApi.getScheduleTimes(cohort.id),
        intakeApi.getStats(cohort.id),
      ]);

      setPaperforms(formsRes);
      setMergeTags(tagsRes.length > 0 ? tagsRes : DEFAULT_MERGE_TAGS);
      setDefaultTemplates(templatesRes);
      setScheduleTimes(timesRes);
      setParticipantCount(statusRes.participant_count);
      setIntakeStats(statsRes);

      // Map feedback configs by composite key
      const newFeedbackConfigs: Record<string, FeedbackSurveyConfig | null> = {
        [postSessionKey('group_coaching')]: null,
        [postSessionKey('workshop')]: null,
        midpoint: null,
        endpoint: null,
      };
      configsRes.forEach((config) => {
        if (config.survey_type === 'post_session') {
          newFeedbackConfigs[postSessionKey(config.modality_type)] = config;
        } else {
          newFeedbackConfigs[config.survey_type] = config;
        }
      });
      setFeedbackConfigs(newFeedbackConfigs);

      // Initialize form states from existing configs
      const newFormStates: Record<string, SurveyFormState> = {
        // Matching Form — from intake stats
        matching_form: {
          paperform_id: statsRes.intake_form_id || '',
          email_subject: DEFAULT_SUBJECTS.matching_form,
          email_template: DEFAULT_BODIES.matching_form,
          survey_deadline: null,
          auto_send: true,
          isDirty: false,
          isSaved: !!statsRes.intake_form_id,
        },
        // Post Session — per modality
        [postSessionKey('group_coaching')]: initFeedbackFormState(
          'post_session',
          newFeedbackConfigs[postSessionKey('group_coaching')],
          templatesRes,
        ),
        [postSessionKey('workshop')]: initFeedbackFormState(
          'post_session',
          newFeedbackConfigs[postSessionKey('workshop')],
          templatesRes,
        ),
        midpoint: initFeedbackFormState('midpoint', newFeedbackConfigs.midpoint, templatesRes),
        endpoint: initFeedbackFormState('endpoint', newFeedbackConfigs.endpoint, templatesRes),
      };
      setFormStates(newFormStates);
    } catch (error) {
      console.error('Failed to load survey data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load survey configuration data',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohort.id]);

  function initFeedbackFormState(
    type: FeedbackSurveyType,
    config: FeedbackSurveyConfig | null,
    templates: Record<FeedbackSurveyType, EmailTemplate> | null,
  ): SurveyFormState {
    if (config) {
      return {
        paperform_id: config.paperform_id || '',
        email_subject: config.email_subject || '',
        email_template: config.email_template || '',
        // MED-1265: parseDateOnly, not new Date() — the raw parse lands a day early
        // for viewers west of UTC, and saving then wrote the shifted date back.
        survey_deadline: config.survey_deadline ? parseDateOnly(config.survey_deadline) : null,
        auto_send: config.auto_send,
        isDirty: false,
        isSaved: true,
      };
    }
    const typeKey = type as SurveyType;
    const defaultTemplate = templates?.[type];
    return {
      paperform_id: '',
      email_subject: defaultTemplate?.subject || DEFAULT_SUBJECTS[typeKey] || '',
      email_template: defaultTemplate?.body || DEFAULT_BODIES[typeKey] || '',
      survey_deadline: null,
      auto_send: true,
      isDirty: false,
      isSaved: false,
    };
  }

  useEffect(() => {
    if (isActive) {
      loadData();
    }
  }, [isActive, loadData]);

  // Sync URL survey param changes
  useEffect(() => {
    if (surveyParam && SURVEY_TYPE_OPTIONS.some((o) => o.value === surveyParam)) {
      setActiveSurveyType(surveyParam);
    }
  }, [surveyParam]);

  // === Save Handlers ===

  const handleSaveMatchingForm = async () => {
    const state = formStates.matching_form;
    if (!state.paperform_id) {
      notifications.show({ title: 'Validation Error', message: 'Please select a form', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      await intakeApi.configureForm(cohort.id, { form_id: state.paperform_id });
      setFormStates((prev) => ({
        ...prev,
        matching_form: { ...prev.matching_form, isDirty: false, isSaved: true },
      }));
      setIntakeStats((prev) => prev ? { ...prev, intake_form_id: state.paperform_id, intake_configured_at: new Date().toISOString() } : prev);
      notifications.show({ title: 'Configuration Saved', message: 'Matching Form configuration saved', color: 'green' });
    } catch (error) {
      notifications.show({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to save configuration', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeedbackSurvey = async () => {
    const state = formStates[currentFormKey];
    if (!state?.paperform_id) {
      notifications.show({ title: 'Validation Error', message: 'Please select a form', color: 'red' });
      return;
    }
    if (!state.email_subject.trim()) {
      notifications.show({ title: 'Validation Error', message: 'Please enter an email subject', color: 'red' });
      return;
    }
    if (!state.email_template.trim()) {
      notifications.show({ title: 'Validation Error', message: 'Please enter an email message', color: 'red' });
      return;
    }

    setSaving(true);
    try {
      const surveyType = activeSurveyType as FeedbackSurveyType;
      const savedConfig = await feedbackSurveysApi.upsertConfig(cohort.id, surveyType, {
        paperform_id: state.paperform_id,
        email_subject: state.email_subject,
        email_template: state.email_template,
        survey_deadline: toISODateString(state.survey_deadline) ?? null,
        auto_send: state.auto_send,
        ...(surveyType === 'post_session' && { modality_type: activePostSessionModality }),
      });

      // For midpoint/endpoint, also schedule if auto_send
      if ((surveyType === 'midpoint' || surveyType === 'endpoint') && state.auto_send) {
        await feedbackSurveysApi.scheduleSurvey(
          cohort.id,
          surveyType,
          toISODateString(state.survey_deadline) ?? null,
        );
      }

      setFeedbackConfigs((prev) => ({ ...prev, [currentFormKey]: savedConfig }));
      setFormStates((prev) => ({
        ...prev,
        [currentFormKey]: { ...prev[currentFormKey], isDirty: false, isSaved: true },
      }));

      // Reload schedule times to get updated scheduled_for
      const timesRes = await feedbackSurveysApi.getScheduleTimes(cohort.id);
      setScheduleTimes(timesRes);

      // Reload configs for badge state
      const configsRes = await feedbackSurveysApi.getConfigs(cohort.id);
      const newFeedbackConfigs: Record<string, FeedbackSurveyConfig | null> = {
        [postSessionKey('group_coaching')]: null,
        [postSessionKey('workshop')]: null,
        midpoint: null,
        endpoint: null,
      };
      configsRes.forEach((config) => {
        if (config.survey_type === 'post_session') {
          newFeedbackConfigs[postSessionKey(config.modality_type)] = config;
        } else {
          newFeedbackConfigs[config.survey_type] = config;
        }
      });
      setFeedbackConfigs(newFeedbackConfigs);

      // MED-778: Different toast copy when features are OFF
      const featuresOff = cohort.participant_features_enabled === false;
      const label = featuresOff
        ? 'Survey configured. Custom participant URLs are generated and available in the export. No sends will be scheduled while features are off.'
        : activeSurveyType === 'midpoint' || activeSurveyType === 'endpoint'
          ? 'Configuration saved & surveys scheduled'
          : 'Survey configuration saved';
      notifications.show({
        title: 'Configuration Saved',
        message: label,
        color: featuresOff ? 'warningAmber' : 'green',
        autoClose: featuresOff ? 6000 : 3000,
      });
    } catch (error) {
      notifications.show({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to save configuration', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryAction = () => {
    if (activeSurveyType === 'matching_form') {
      handleSaveMatchingForm();
    } else {
      handleSaveFeedbackSurvey();
    }
  };

  // === Test Email ===
  const handleSendTestEmail = async () => {
    const state = formStates[currentFormKey];
    if (!state?.paperform_id) return;

    setTestEmailSending(true);
    try {
      if (activeSurveyType === 'matching_form') {
        await intakeApi.sendTestEmail(cohort.id, {
          form_id: state.paperform_id,
          subject: state.email_subject,
          body: state.email_template,
        });
      } else {
        await feedbackSurveysApi.getPreview(cohort.id, activeSurveyType as FeedbackSurveyType, {
          email_subject: state.email_subject,
          email_template: state.email_template,
          survey_deadline: toISODateString(state.survey_deadline) ?? undefined,
        });
      }
      notifications.show({ title: 'Test Email Sent', message: 'Check your inbox', color: 'green' });
    } catch (error) {
      notifications.show({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to send test email', color: 'red' });
    } finally {
      setTestEmailSending(false);
    }
  };

  // === Clear Configuration ===
  const handleClearConfiguration = async () => {
    setClearing(true);
    try {
      if (activeSurveyType === 'matching_form') {
        await intakeApi.clearConfiguration(cohort.id);
        setIntakeStats((prev) => prev ? { ...prev, intake_form_id: null, intake_configured_at: null } : prev);
        setFormStates((prev) => ({
          ...prev,
          matching_form: { ...makeEmptyState('matching_form'), isDirty: false, isSaved: false },
        }));
      } else {
        const surveyType = activeSurveyType as FeedbackSurveyType;
        // Cancel schedule first if midpoint/endpoint
        if (surveyType === 'midpoint' || surveyType === 'endpoint') {
          const config = feedbackConfigs[currentFormKey];
          if (config?.schedule_status === 'scheduled') {
            await feedbackSurveysApi.cancelSchedule(cohort.id, surveyType);
          }
        }
        await feedbackSurveysApi.deleteConfig(
          cohort.id,
          surveyType,
          surveyType === 'post_session' ? activePostSessionModality : undefined,
        );
        setFeedbackConfigs((prev) => ({ ...prev, [currentFormKey]: null }));
        const typeKey = activeSurveyType as SurveyType;
        const defaultTemplate = defaultTemplates?.[surveyType];
        setFormStates((prev) => ({
          ...prev,
          [currentFormKey]: {
            paperform_id: '',
            email_subject: defaultTemplate?.subject || DEFAULT_SUBJECTS[typeKey] || '',
            email_template: defaultTemplate?.body || DEFAULT_BODIES[typeKey] || '',
            survey_deadline: null,
            auto_send: true,
            isDirty: false,
            isSaved: false,
          },
        }));

        // Refresh schedule times
        const timesRes = await feedbackSurveysApi.getScheduleTimes(cohort.id);
        setScheduleTimes(timesRes);
      }

      closeClearModal();
      notifications.show({ title: 'Configuration Cleared', message: 'Survey configuration has been cleared', color: 'green' });
    } catch (error) {
      notifications.show({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to clear configuration', color: 'red' });
    } finally {
      setClearing(false);
    }
  };

  // === MED-942: Pull Historical Responses ===
  const handlePullHistoricalResponses = async () => {
    const state = formStates.matching_form;
    if (!state?.paperform_id) return;

    // Open modal in loading state immediately so the user gets feedback even
    // on slow networks; the modal owns its own loading/error/success branching.
    setBackfillSummary(null);
    setBackfillError(null);
    setBackfillLoading(true);
    openBackfillModal();

    try {
      const summary = await intakeApi.pullHistoricalResponses(
        cohort.id,
        state.paperform_id,
      );
      setBackfillSummary(summary);

      // AC #21 mirror: surface a brief notification beyond the modal so the
      // CM gets a passive confirmation even if they dismissed the modal early.
      const newlyMatched = summary.matched.length;
      const unmatched = summary.unmatched.length;
      const ambiguous = summary.ambiguous.length;
      const noEmail = summary.no_email.length;
      const failed = summary.failed.length;
      const newlyTouched = newlyMatched + unmatched + ambiguous + noEmail + failed;

      if (newlyTouched === 0) {
        notifications.show({
          title: 'Nothing new to import',
          message:
            summary.total_submissions_fetched === 0
              ? 'Paperform returned no submissions for this form.'
              : `All ${summary.total_submissions_fetched} submissions were already imported.`,
          color: 'gray',
        });
      } else if (failed > 0) {
        notifications.show({
          title: 'Backfill completed with errors',
          message: `Matched ${newlyMatched}, failed ${failed}. See modal for details.`,
          color: 'warningAmber',
        });
      } else if (newlyMatched === 0) {
        // Surfaced during MED-942 validation: when nothing matched but the
        // run did surface submissions needing CM attention (unmatched /
        // ambiguous / no-email), a green "Matched and imported 0" was
        // misleadingly rosy. Use an amber toast that names the unactioned
        // count and points to the modal for detail.
        const needsAttention = unmatched + ambiguous + noEmail;
        notifications.show({
          title: 'Backfill complete — needs review',
          message: `0 matched. ${needsAttention} submission${needsAttention === 1 ? '' : 's'} need attention. See modal for details.`,
          color: 'warningAmber',
        });
      } else {
        notifications.show({
          title: 'Backfill complete',
          message: `Matched and imported ${newlyMatched} submission${newlyMatched === 1 ? '' : 's'}.`,
          color: 'green',
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to pull historical responses';
      setBackfillError(message);
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleCloseBackfillModal = () => {
    closeBackfillModal();
    // Clear summary/error on close so re-opening the modal for a fresh run
    // doesn't briefly flash stale results.
    setBackfillSummary(null);
    setBackfillError(null);
  };

  // === Toggle Options with indicators ===
  const buildToggleOptions = (): ButtonToggleOption<SurveyType>[] => {
    const isMatchingFormConfigured = !!intakeStats?.intake_form_id;
    const gcConfig = feedbackConfigs[postSessionKey('group_coaching')];
    const wsConfig = feedbackConfigs[postSessionKey('workshop')];
    const isPostSessionConfigured = !!(gcConfig || wsConfig);
    const midpointConfig = feedbackConfigs.midpoint;
    const isMidpointConfigured = !!midpointConfig;
    const isMidpointScheduled = midpointConfig?.schedule_status === 'scheduled';
    const endpointConfig = feedbackConfigs.endpoint;
    const isEndpointConfigured = !!endpointConfig;
    const isEndpointScheduled = endpointConfig?.schedule_status === 'scheduled';

    return [
      {
        value: 'matching_form' as SurveyType,
        label: 'Matching Form',
        indicator: isMatchingFormConfigured,
      },
      {
        value: 'post_session' as SurveyType,
        label: 'Post Session',
        indicator: isPostSessionConfigured,
      },
      {
        value: 'midpoint' as SurveyType,
        label: 'Midpoint',
        indicator: isMidpointConfigured,
        statusBadge: isMidpointScheduled ? 'SCHEDULED' : undefined,
      },
      {
        value: 'endpoint' as SurveyType,
        label: 'Endpoint',
        indicator: isEndpointConfigured,
        statusBadge: isEndpointScheduled ? 'SCHEDULED' : undefined,
      },
    ];
  };

  // === Info Banner Content ===
  const getInfoBanner = () => {
    const baseText = INFO_BANNERS[activeSurveyType];

    if (activeSurveyType === 'midpoint' || activeSurveyType === 'endpoint') {
      const timeInfo = activeSurveyType === 'midpoint' ? scheduleTimes?.midpoint : scheduleTimes?.endpoint;
      const config = feedbackConfigs[activeSurveyType];
      if (timeInfo && config?.schedule_status === 'scheduled') {
        return (
          <>
            <Text size="sm">{baseText}</Text>
            <Text size="sm" fw={600} mt={4}>
              Scheduled to send: {new Date(timeInfo.scheduled_for).toLocaleString()}
              {' '}(after Session {timeInfo.session_number})
            </Text>
          </>
        );
      }
    }

    return <Text size="sm">{baseText}</Text>;
  };

  // === Primary action label ===
  const getPrimaryActionLabel = () => {
    if (activeSurveyType === 'midpoint' || activeSurveyType === 'endpoint') {
      return 'Save Configuration & Schedule Surveys';
    }
    return 'Save Configuration';
  };

  // === Post Session header content ===
  const getPostSessionHeader = () => {
    if (activeSurveyType !== 'post_session') return undefined;

    return (
      <Stack gap="sm">
        {/* Modality toggle */}
        {postSessionModalities.length > 1 && (
          <SegmentedControl
            value={activePostSessionModality}
            onChange={(value) => setActivePostSessionModality(value as SurveyModalityType)}
            data={[
              ...(postSessionModalities.includes('group_coaching') ? [{ label: 'Group Coaching', value: 'group_coaching' }] : []),
              ...(postSessionModalities.includes('workshop') ? [{ label: 'Workshop', value: 'workshop' }] : []),
            ]}
            color="medleyGreen"
            fullWidth
          />
        )}

        {/* 1:1 info callout */}
        {hasOneOnOne && (
          <Alert icon={<Info size={16} />} color="gray" variant="light">
            <Text size="sm">
              No post-session surveys for 1:1 coaching sessions. Surveys are only configured for Group Coaching and Workshops.
            </Text>
          </Alert>
        )}

        {/* Auto-send checkbox — MED-778: hidden when participant features are OFF */}
        {cohort.participant_features_enabled === false ? (
          <Alert icon={<Info size={16} />} color="gray" variant="light">
            <Text size="sm" fs="italic">
              Surveys are configured for export — Medley will not auto-send while features are off.
              Distribute participant URLs through your channels.
            </Text>
          </Alert>
        ) : (
          <Checkbox
            label="Automatically Send Configured Post-Session Form"
            checked={currentState.auto_send}
            onChange={(e) => updateFormState(currentFormKey, { auto_send: e.currentTarget.checked })}
          />
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {/* Survey Type Toggle */}
      <ButtonToggle<SurveyType>
        options={buildToggleOptions()}
        value={activeSurveyType}
        onChange={handleSurveyTypeChange}
      />

      {/* Survey Config Card */}
      <SurveyConfigCard
        infoBanner={getInfoBanner()}
        state={currentState}
        updateState={(updates) => updateFormState(currentFormKey, updates)}
        paperforms={paperforms}
        mergeTags={mergeTags}
        participantCount={participantCount}
        cohortName={cohort.name}
        showDeadline={activeSurveyType !== 'post_session'}
        primaryActionLabel={getPrimaryActionLabel()}
        onPrimaryAction={handlePrimaryAction}
        primaryActionLoading={saving}
        onSendTestEmail={handleSendTestEmail}
        testEmailSending={testEmailSending}
        onClearConfiguration={openClearModal}
        headerContent={getPostSessionHeader()}
        secondaryActionBeforeTestEmail={
          // AC #4: ONLY on the Matching Form sub-tab. The card itself stays
          // generic; the sub-tab decides whether to fill the slot.
          activeSurveyType === 'matching_form' ? (
            <Tooltip
              // AC #3: explain why the button is disabled. The Tooltip
              // renders unconditionally so screen readers always see it,
              // but it only appears visually when the user hovers.
              label="Select a form first to pull existing responses."
              disabled={!!currentState.paperform_id}
              withArrow
            >
              {/* AC #2: enabled iff a form is selected in the dropdown. */}
              {/* AC #1: secondary outlined styling, matches Send Test Email. */}
              <Button
                variant="outline"
                color="gray"
                bg="white"
                style={{ borderColor: '#E8E6E3' }}
                onClick={handlePullHistoricalResponses}
                disabled={!currentState.paperform_id || backfillLoading}
                loading={backfillLoading}
                leftSection={<Download size={14} />}
              >
                Pull Historical Responses
              </Button>
            </Tooltip>
          ) : undefined
        }
      />

      {/* Clear Configuration Modal */}
      <ClearConfigurationModal
        opened={clearModalOpened}
        onClose={closeClearModal}
        onConfirm={handleClearConfiguration}
        surveyType={activeSurveyType}
        loading={clearing}
      />

      {/* MED-942: Pull Historical Responses result modal */}
      <PullHistoricalResponsesModal
        opened={backfillModalOpened}
        onClose={handleCloseBackfillModal}
        loading={backfillLoading}
        summary={backfillSummary}
        error={backfillError}
      />
    </Stack>
  );
}
